import { forwardRef } from 'react'
import { Input, Label } from 'reactstrap'
import agency from '../../../api/agency/hooks'
import { Contact } from '../../../api/contacts/types'
import { DepthAnalysisOptions, DepthAnalysisQuestions, DepthAnalysisSections } from '../../../api/inDepthAnalysis/types'

interface InDepthAnalysisPrintFormProps {
    sections: DepthAnalysisSections[]
    contact: Contact | null | undefined
    formTitle?: string
}

const InDepthAnalysisPrintForm = forwardRef<HTMLDivElement, InDepthAnalysisPrintFormProps>((props, ref) => {
    const { data: agencyData } = agency.GetAgency.useQuery()

    const { sections, contact, formTitle } = props

    const isPerson = contact?.contactType === 'person'
    const personPrintBreakIds = [2, 4, 10, 14]
    const companyPrintBreakIds = [18, 21, 23]
    const printSectionBreakIds = isPerson ? personPrintBreakIds : companyPrintBreakIds

    const renderQuestion = (question: DepthAnalysisQuestions) => {
        const { id, text, type, required, options } = question

        switch (type) {
            case 'boolean':
                return (
                    <div key={id} className="d-flex align-items-start justify-content-between p-2 border-bottom">
                        <span style={{ width: '70%' }}>
                            {text}
                            {required && <span style={{ color: '#e82c2c' }}>*</span>}
                        </span>
                        <span style={{ width: '30%' }} className="d-flex justify-content-end">
                            {options.map((option: DepthAnalysisOptions, index: number) => (
                                <span key={index} className="d-flex align-items-center me-3">
                                    <Input type="checkbox" checked={false} readOnly className="me-1" />
                                    <Label className="fs-12 mb-0">{option.label}</Label>
                                </span>
                            ))}
                        </span>
                    </div>
                )
            case 'string':
            case 'float':
                return (
                    <div key={id} className="d-flex align-items-end justify-content-between p-2 border-bottom">
                        <span style={{ width: '50%' }} className="fs-12">
                            {text}
                            {required && <span style={{ color: '#e82c2c' }}>*</span>}:
                        </span>
                        <div style={{ width: '50%' }} className="border-bottom border-primary"></div>
                    </div>
                )
            case 'text':
                return (
                    <div key={id} className="p-3 border-bottom">
                        <div className="fs-12 mb-5">
                            {text}
                            {required && <span style={{ color: '#e82c2c' }}>*</span>}:
                        </div>
                        <div className="w-100 mb-5 border-bottom border-primary"></div>
                        <div className="w-100 mb-5 border-bottom border-primary"></div>
                        <div className="w-100 mb-5 border-bottom border-primary"></div>
                        <div className="w-100 border-bottom border-primary"></div>
                    </div>
                )
            case 'date':
                return (
                    <div key={id} className="d-flex align-items-center justify-content-between p-2 border-bottom">
                        <span style={{ width: '50%' }} className="fs-12">
                            {text}
                            {required && <span style={{ color: '#e82c2c' }}>*</span>}:
                        </span>
                        <div style={{ width: '50%' }} className="border-bottom border-primary text-muted fs-10">
                            (DD-MM-YYYY)
                        </div>
                    </div>
                )
            case 'select':
                return (
                    <div key={id} className="d-flex justify-content-between p-2 border-bottom">
                        <div className="fs-12 mb-2">
                            {text}
                            {required && <span style={{ color: '#e82c2c' }}>*</span>}:
                        </div>
                        <div className="d-flex flex-wrap">
                            {options.map((option: DepthAnalysisOptions, index: number) => (
                                <span key={index} className="d-flex align-items-center me-3 mb-2">
                                    <Input type="checkbox" checked={false} readOnly className="me-1" />
                                    <Label className="fs-12 mb-0">{option.label}</Label>
                                </span>
                            ))}
                        </div>
                    </div>
                )

            default:
                return null
        }
    }

    return (
        <main ref={ref}>
            {/* Header with Agency Info */}
            <header className={`d-flex justify-content-${agencyData?.logo ? 'around' : 'end'} align-items-center bg-white mb-3 p-2 border`}>
                {agencyData?.logo && (
                    <img
                        src={agencyData.logo}
                        alt="Agency Logo"
                        style={{
                            maxHeight: '100px',
                            width: 'auto',
                            objectFit: 'contain',
                        }}
                    />
                )}
                <div className="fs-10">
                    <div>{`${agencyData?.name}, ${agencyData?.street} ${agencyData?.streetNumber}`}</div>
                    <div>{`${agencyData?.zipCode} ${agencyData?.city}, ${agencyData?.country}; tel: ${agencyData?.phoneNumber}`}</div>
                    <div>{`Email: ${agencyData?.email}`}</div>
                    <div>{`Web: ${agencyData?.websiteUrl || ''}`}</div>
                </div>
            </header>

            {/* Form Title */}
            <div className="mb-3 p-2 border">
                <h4 className="fw-bold mb-2">{formTitle}</h4>
                <div className="fs-6 me-3">
                    {isPerson ? (
                        <div>
                            <span className="fw-medium">Kontakt: </span>
                            {contact?.contactPerson?.firstName} {contact?.contactPerson?.lastName}
                        </div>
                    ) : (
                        <div>
                            <span className="fw-medium">Poduzeće: </span>
                            {contact?.contactCompany?.name}
                        </div>
                    )}
                </div>
            </div>

            {/* Sections and Questions */}
            {sections
                ?.sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))
                ?.map((section: DepthAnalysisSections) => {
                    return (
                        <div
                            key={`section_${section.id}`}
                            style={{ marginBottom: `${[...printSectionBreakIds].includes(section.id) ? '200px' : '10px'}` }}
                        >
                            <div className="p-3 border">
                                <h5 className="fw-bold">{section.title}</h5>
                                <div>
                                    {section.questions
                                        ?.sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))
                                        ?.map((question: DepthAnalysisQuestions) => {
                                            return renderQuestion(question)
                                        })}
                                </div>
                            </div>
                        </div>
                    )
                })}

            {/* Footer */}
            <footer className="mb-3 p-3 border">
                <div className="d-flex flex-wrap">
                    <div className="d-flex align-items-end mb-2" style={{ width: '50%' }}>
                        <span className="fs-12 me-3">Potpis agenta:</span>
                        <div style={{ width: '50%' }} className="border-bottom border-primary"></div>
                    </div>
                    <div className="d-flex align-items-end mb-2" style={{ width: '50%' }}>
                        <span className="fs-12 me-3">Datum:</span>
                        <div style={{ width: '50%' }} className="border-bottom border-primary"></div>
                    </div>
                </div>
            </footer>
        </main>
    )
})

export default InDepthAnalysisPrintForm
